/**
 * Scroll-linked micro-choreography for the home page.
 *
 * SettleIn — a section that arrives like an event: scale 0.965→1, y 28→0 as it
 * scrolls into view (the dark closing band). Capped amplitudes per the motion
 * contract; transform-only so it stays compositor-cheap.
 *
 * ParallaxDrift — one plane of depth: the child lags the scroll by up to
 * `drift` px (hero status panel, 14px cap).
 *
 * Both render a plain div under prefers-reduced-motion.
 */

import { useRef, type ReactNode } from 'react'
import { motion, useScroll, useSpring, useTransform } from 'framer-motion'
import { useReducedMotion } from './useReducedMotion'

interface SettleInProps {
  children: ReactNode
  className?: string
}

export function SettleIn({ children, className }: SettleInProps) {
  const prefersReducedMotion = useReducedMotion()
  const ref = useRef<HTMLDivElement>(null)
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start end', 'start center'],
  })
  const scale = useTransform(scrollYProgress, [0, 1], [0.965, 1])
  const y = useTransform(scrollYProgress, [0, 1], [28, 0])

  if (prefersReducedMotion) {
    return <div ref={ref} className={className}>{children}</div>
  }

  return (
    <motion.div ref={ref} className={className} style={{ scale, y }}>
      {children}
    </motion.div>
  )
}

interface ParallaxDriftProps {
  children: ReactNode
  className?: string
  /** Max upward drift in px as the container scrolls away (default 14). */
  drift?: number
}

export function ParallaxDrift({ children, className, drift = 14 }: ParallaxDriftProps) {
  const prefersReducedMotion = useReducedMotion()
  const ref = useRef<HTMLDivElement>(null)
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start start', 'end start'],
  })
  const y = useTransform(scrollYProgress, [0, 1], [0, -drift])

  if (prefersReducedMotion) {
    return <div ref={ref} className={className}>{children}</div>
  }

  return (
    <motion.div ref={ref} className={className} style={{ y }}>
      {children}
    </motion.div>
  )
}


interface MaskRevealInViewProps {
  children: ReactNode
  className?: string
}

/** Whole-block masked reveal: content slides up from behind an overflow-hidden
 *  wrapper when scrolled into view, once. (Per-line splitting deliberately
 *  avoided — it breaks responsive line wrapping.) */
export function MaskRevealInView({ children, className }: MaskRevealInViewProps) {
  const prefersReducedMotion = useReducedMotion()

  if (prefersReducedMotion) {
    return <div className={className}>{children}</div>
  }

  return (
    <div className={className} style={{ overflow: 'hidden' }}>
      <motion.div
        initial={{ y: '100%', opacity: 0 }}
        whileInView={{ y: 0, opacity: 1 }}
        viewport={{ once: true, margin: '-80px' }}
        transition={{ duration: 0.6, ease: [0.25, 1, 0.5, 1] }}
      >
        {children}
      </motion.div>
    </div>
  )
}

/** 2px reading-progress line pinned under the fixed header (blog posts).
 *  Communicates position, so it stays under reduced motion (spring off). */
export function ReadingProgress() {
  const prefersReducedMotion = useReducedMotion()
  const { scrollYProgress } = useScroll()
  const smooth = useSpring(scrollYProgress, { stiffness: 220, damping: 40, restDelta: 0.001 })

  return (
    <motion.div
      className="rw-reading-progress"
      style={{ scaleX: prefersReducedMotion ? scrollYProgress : smooth }}
      aria-hidden="true"
    />
  )
}
